import {
  Box,
  Checkbox,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { formatSettings, findSettingForEntryDate } from "./helpers";
import { isFulfilled } from "@reduxjs/toolkit";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getWorkTimeDuration } from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DayOfWeekDate from "../../../../common/components/functional/DayOfWeekDate/DayOfWeekDate";
import isEmpty from "../../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { fetchAssignmentById } from "../../../talents/talentsSlice";
import { EditEntry } from "../../entriesAPI";
import LunchBreakFields from "../Shared/LunchBreakFields";
import StartEndTimeFields from "../Shared/StartEndTimeFields";

interface Props {
  entry: EditEntry | null;
  setEntry: (val: EditEntry) => void;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
}

const BasicInfo = ({ entry, setEntry, inputErrors, setInputErrors }: Props) => {
  const [costCenterOptions, setCostCenterOptions] = useState<
    AutocompleteType[]
  >([]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const assignment = useAppSelector((state) => state.talents.assignment);
  const timeBankSettings = assignment?.timeBankSettings;

  const formattedTimeBankSettings = timeBankSettings
    ? formatSettings(timeBankSettings, assignment?.details.end)
    : [];
  const formattedFlexiHoursSettings = assignment?.flexibleHoursSettings
    ? formatSettings(assignment.flexibleHoursSettings, assignment?.details.end)
    : [];

  // Find time bank and flexi hour settings that are used for the day of the entry
  const bankSettingForEntryTime = findSettingForEntryDate(
    formattedTimeBankSettings,
    entry,
  );
  const flexiHoursSettingForEntryTime = findSettingForEntryDate(
    formattedFlexiHoursSettings,
    entry,
  );

  const allowSelectTransferToBank =
    entry &&
    !entry.isShift &&
    !!bankSettingForEntryTime &&
    !!bankSettingForEntryTime.mode;
  const allowSelectFlexibleHours =
    entry &&
    !entry.isShift &&
    !!flexiHoursSettingForEntryTime &&
    !!flexiHoursSettingForEntryTime.mode;

  // For new entries that have valid flexible hour settings, default to using flexible hours
  const isUseFlexiHoursChecked =
    entry?.flexibleHours === undefined && !!flexiHoursSettingForEntryTime?.mode
      ? true
      : entry?.flexibleHours;

  useEffect(() => {
    if (entry?.assignmentId) {
      dispatch(fetchAssignmentById(entry.assignmentId)).then((action) => {
        if (isFulfilled(action)) {
          if (
            action.payload.details.custCostCenters &&
            isEmpty(costCenterOptions)
          ) {
            // cost center options from assignment + cost center from entry
            const formattedCostCentersOptions = [
              ...action.payload.details.custCostCenters,
            ];

            if (entry.customerCostCenterId) {
              formattedCostCentersOptions.push({
                id: entry.customerCostCenterId,
                name: entry.customerCostCenterName,
              });
            }

            // remove dublicates
            const uniquecostCenterOptions = formattedCostCentersOptions.filter(
              (value, index, array) =>
                index ===
                array.findIndex((costCenter) => costCenter.id === value.id),
            );

            setCostCenterOptions(uniquecostCenterOptions);
          }

          // For long assignment entries, check if default lunch break exists
          let defaultLunchBreak = entry.defaultLunchBreak;
          if (
            !action.payload.isShift &&
            action.payload.details.defaultLunchBreak
          ) {
            const { defaultLunchBreak: defaultLunch } = action.payload.details;
            defaultLunchBreak = defaultLunch;
          }

          // By default for new entries, set flexible hours to true if flexible hour settings exist
          let flexibleHours = entry.flexibleHours;
          const flexiHourSettings = action.payload.flexibleHoursSettings;
          const formattedFlexiHoursSettings = flexiHourSettings
            ? formatSettings(flexiHourSettings, assignment?.details.end)
            : [];
          const flexiHoursSettingForEntryTime = findSettingForEntryDate(
            formattedFlexiHoursSettings,
            entry,
          );
          if (
            !action.payload.isShift &&
            !!flexiHoursSettingForEntryTime?.mode &&
            entry?.flexibleHours === undefined
          ) {
            flexibleHours = true;
          }

          setEntry({
            ...entry,
            defaultLunchBreak,
            flexibleHours,
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, entry?.assignmentId, setEntry]);

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const updateShiftTime = (updatedData: { start: number; end: number }) => {
    const updatedEntry = {
      ...entry,
      start: updatedData.start,
      end: updatedData.end,
    } as EditEntry;

    setEntry(updatedEntry);
  };

  const updateCostCenter = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const selectedCostCenter = costCenterOptions.find(
      (costCenter) => costCenter.id === e.target.value,
    );

    if (selectedCostCenter) {
      const updatedEntry = {
        ...entry,
        customerCostCenterId: selectedCostCenter.id,
        customerCostCenterName: selectedCostCenter.name,
      } as EditEntry;
      setEntry(updatedEntry);
    }
  };

  const toggleWorkTimeBankTransfer = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedEntry = {
      ...entry,
      maxHoursToBank: event.target.checked ? "24" : null,
      flexibleHours: event.target.checked ? false : entry?.flexibleHours,
    } as EditEntry;
    setEntry(updatedEntry);
  };

  const toggleFlexibleHours = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedEntry = {
      ...entry,
      flexibleHours: event.target.checked,
      maxHoursToBank: event.target.checked ? null : entry?.maxHoursToBank,
    } as EditEntry;
    setEntry(updatedEntry);
  };

  return (
    entry && (
      <Grid
        item
        xs={4}
        margin={0}
        padding={0}
        sx={{
          paddingRight: "15px",
        }}
      >
        <DialogTitle
          sx={{
            margin: "0px",
            padding: "0px",
          }}
        >
          <Typography
            variant="header"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("hourEntries:basicInfo").toUpperCase()}
          </Typography>
          <Divider
            sx={{
              marginTop: "16px",
              marginBottom: "16px",
            }}
          />
        </DialogTitle>
        <DayOfWeekDate start={entry.start} backgroundColor="#7A4CC3" />
        <Box
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontWeight: "700",
            marginTop: "16px",
            marginBottom: "4px",
          }}
        >
          {`${entry.workLocationName} - ${
            translatedWorkTitles[entry?.workTitleId]
          }`}
        </Box>
        <Box margin="8px 0 8px 0">
          <TextField
            select
            value={entry.customerCostCenterId}
            size="small"
            fullWidth
            onChange={(e) => updateCostCenter(e)}
          >
            {costCenterOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <StartEndTimeFields
          id={"shift"}
          setInputErrors={setInputErrors}
          inputErrors={inputErrors}
          disabled={false}
          requireValue={true}
          start={entry.start}
          end={entry.end}
          plannedStart={entry.plannedStart}
          update={updateShiftTime}
        />

        <LunchBreakFields
          entry={entry}
          setEntry={setEntry}
          id={"lunchBreak"}
          setInputErrors={setInputErrors}
          inputErrors={inputErrors}
        />

        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            marginTop: "16px",
          }}
        >
          {`${t("hourEntries:actualWorkingTime")}: ${getWorkTimeDuration(
            new Date(entry.end),
            new Date(entry.start),
            entry.lunchBreak?.duration,
          )}`}
        </Box>
        {allowSelectTransferToBank && (
          <Box mt="16px" display="flex" alignItems="center">
            <Checkbox
              checked={!!entry.maxHoursToBank}
              onChange={toggleWorkTimeBankTransfer}
              sx={{
                padding: 0,
                marginRight: "8px",
              }}
            />
            <Typography fontSize={14}>
              {t("hourEntries:transferExcessToWorktimeBank")}
            </Typography>
          </Box>
        )}
        {allowSelectFlexibleHours && (
          <Box mt="16px" display="flex" alignItems="center">
            <Checkbox
              checked={isUseFlexiHoursChecked}
              onChange={toggleFlexibleHours}
              sx={{
                padding: 0,
                marginRight: "8px",
              }}
            />
            <Typography fontSize={14}>
              {t("hourEntries:useFlexibleHours")}
            </Typography>
          </Box>
        )}
      </Grid>
    )
  );
};

export default BasicInfo;
